import React, { useMemo, useState } from 'react'
import { vhMinus } from 'utils/textZoom'
import dayjs from 'dayjs'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm, FieldValues, Controller, FormProvider } from 'react-hook-form'

import { getErrorMessage } from 'utils'
import { useSnackbar } from 'contexts'
import { useOwnerCreateManualMutation } from 'hooks/api'
import { ROUTES } from 'routes'

import { Box, Collapse, Typography } from '@mui/material'
import { CircleAlertIcon } from 'lucide-react'

import { Button, Card, Alert, SectionNav } from '@repo/ui'
import { Header, Search, CheckboxField, StickyFormHeader, TocSpacer } from 'components'

import { OWNER_FORM_SECTIONS } from './ownerFormShared'
// Section wrapper styles, shared with the product + owner edit forms so the three cannot drift.
import { FORM_SECTION_SX } from 'utils/formSectionSx'
import { useSectionScrollSpy } from './useSectionScrollSpy'
import { OwnerDetailsFields } from './OwnerDetailsFields'
import { OwnerAddressFields } from './OwnerAddressFields'
import { OwnerMarketingFields } from './OwnerMarketingFields'

const COLUMN_WIDTH = 720

// VERA-914 (Stage B) — Create Owner, expanded into a section-driven form (left
// TOC + search bar) matching ProductEditPage's pattern. Details / Address /
// Marketing sections are shared verbatim with OwnerEditPage (see
// OwnerDetailsFields / OwnerAddressFields / OwnerMarketingFields) so the two
// forms stay consistent going forward.
export const CreateOwnerPage = () => {
  const [inviteOwner, setInviteOwner] = useState(false)
  const [search, setSearch] = useState('')
  const navigate = useNavigate()
  const { open: notification } = useSnackbar()

  // VERA-914 / VRG-105 (Stage B) — Leads tab "Add as owner" kebab action prefills
  // the lead's email (+ name, when known) via query params (?email=&full_name=).
  const [searchParams] = useSearchParams()
  const prefillEmail = searchParams.get('email') || ''
  const prefillFullName = searchParams.get('full_name') || ''

  const methods = useForm({
    defaultValues: {
      full_name: prefillFullName,
      email: prefillEmail,
      phone: '',
      birthday: '',
      gender: '',
      country: '',
      inviteOwner: false,
      photo: null,
      address_line: '',
      city: '',
      state: '',
      postal_code: '',
      address_country: '',
      latitude: null,
      longitude: null,
      place_id: null,
      formatted_address: null,
      email_marketing_consent: false,
      sms_marketing_consent: false,
    },
    mode: 'onChange',
  })

  const {
    handleSubmit,
    formState: { isDirty, isValid, errors },
  } = methods

  const sectionIds = useMemo(() => OWNER_FORM_SECTIONS.map((s) => s.id), [])
  const { activeId, setActiveId, scrollToSection, suppressSpyRef } = useSectionScrollSpy(sectionIds, true)

  const { createOwner, isLoading } = useOwnerCreateManualMutation({
    onSuccess: (data) => {
      setTimeout(() => {
        navigate(ROUTES.OWNERS)
      }, 100)
    },
    onError: (error) => {
      const message = getErrorMessage(error)
      notification({
        type: 'error',
        title: message,
      })
    },
  })

  const onSubmit = handleSubmit(async (data: FieldValues) => {
    // Birthday is picked/displayed as MM/DD/YYYY (DatePickerField's `format`);
    // convert to an ISO date string (YYYY-MM-DD) for the API.
    const birthdayValue =
      data.birthday && dayjs(data.birthday, 'MM/DD/YYYY').isValid()
        ? dayjs(data.birthday, 'MM/DD/YYYY').format('YYYY-MM-DD')
        : undefined

    createOwner({
      full_name: data.full_name,
      birthday: birthdayValue,
      gender: data.gender === '' ? undefined : data.gender,
      email: data.email,
      phone: data.phone,
      country: data.country === '' ? undefined : data.country,
      address_line: data.address_line || undefined,
      city: data.city || undefined,
      state: data.state || undefined,
      postal_code: data.postal_code || undefined,
      address_country: data.address_country === '' ? undefined : data.address_country,
      latitude: data.latitude ?? undefined,
      longitude: data.longitude ?? undefined,
      place_id: data.place_id || undefined,
      formatted_address: data.formatted_address || undefined,
      email_marketing_consent: data.email_marketing_consent,
      sms_marketing_consent: data.sms_marketing_consent,
      invite_owner: Boolean(inviteOwner),
      photo: data.photo || null,
    })
  })

  const onCancel = () => {
    navigate(-1)
  }

  // ---- Filter: which sections are visible for the current search ----------
  const q = search.trim().toLowerCase()
  const isSectionVisible = useMemo(
    () => (section: (typeof OWNER_FORM_SECTIONS)[number]) => {
      if (!q) return true
      const hasError = section.fields.some((f) => Boolean((errors as any)?.[f]))
      if (hasError) return true
      return (
        section.label.toLowerCase().includes(q) ||
        section.keywords.some((k) => k.includes(q) || q.includes(k))
      )
    },
    [q, errors]
  )

  const visibleSections = OWNER_FORM_SECTIONS.filter(isSectionVisible)
  const hasErrors = Object.keys(errors).length > 0
  const sectionHasError = (section: (typeof OWNER_FORM_SECTIONS)[number]) =>
    section.fields.some((f) => Boolean((errors as any)[f]))

  const handleTocChange = (sectionId: string) => {
    suppressSpyRef.current = true
    const section = OWNER_FORM_SECTIONS.find((s) => s.id === sectionId)
    if (section && !isSectionVisible(section)) {
      setSearch('')
      setActiveId(sectionId)
      setTimeout(() => scrollToSection(sectionId), 50)
      return
    }
    scrollToSection(sectionId)
  }

  const sectionSx = FORM_SECTION_SX

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 6,
          width: '100%',
          // Was mb:16 — clearance for the fixed bottom action bar, which is gone.
          mb: 6,
          justifyContent: 'center',
          alignItems: { xs: 'center', md: 'stretch' },
        }}
      >
        <SectionNav
          items={OWNER_FORM_SECTIONS.map((s) => ({
            label: s.label,
            value: s.id,
            endAdornment: sectionHasError(s) ? (
              <Box component="span" sx={{ color: 'error.main', display: 'inline-flex' }}>
                <CircleAlertIcon size={16} strokeWidth={1.5} />
              </Box>
            ) : undefined,
          }))}
          value={activeId}
          onChange={handleTocChange}
          onBack={onCancel}
        />

        <Box
          sx={{
            flex: 1,
            maxWidth: COLUMN_WIDTH,
            width: { xs: '100%', md: 'auto' },
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            minHeight: vhMinus(160),
          }}
        >
          <StickyFormHeader
            title="Create owner"
            actions={
              <>
                <Button variant="secondary" disableElevation onClick={onCancel} disabled={isLoading}>
                  Cancel
                </Button>
                <Button
                  disableElevation
                  disabled={!isDirty || !isValid || hasErrors || isLoading}
                  onClick={onSubmit}
                >
                  {isLoading ? 'Creating...' : 'Create'}
                </Button>
              </>
            }
          >
            <Search onChange={(value: string) => setSearch(value)} fullWidth={true} placeholder="Search fields..." />
          </StickyFormHeader>

          <FormProvider {...methods}>
            {/* DETAILS */}
            <Collapse in={isSectionVisible(OWNER_FORM_SECTIONS[0])} timeout={250} unmountOnExit>
              <Box id="details" sx={sectionSx}>
                <Card title="Details">
                  <OwnerDetailsFields methods={methods} />
                </Card>

                <Box sx={{ mt: 4 }}>
                  <Controller
                    name="inviteOwner"
                    control={methods.control}
                    render={({ field }) => (
                      <CheckboxField
                        {...methods}
                        onChange={() => {
                          const newValue = !inviteOwner
                          setInviteOwner(newValue)
                          field.onChange(newValue)
                        }}
                        checked={inviteOwner}
                        name="inviteOwner"
                        label="Send invite to the provided email immediately after owner creation"
                      />
                    )}
                  />
                </Box>

                <Box sx={{ mt: 4 }}>
                  <Alert
                    severity="info"
                    // Names the button by its CURRENT label. It said "Create Owner", which
                    // stopped matching when the button shortened to "Create" — helper text
                    // that quotes a control has to be checked whenever that control is retitled.
                    description="Clicking Create will register the account and send an invitation to the provided email. The user must accept the invitation to activate their account."
                  />
                </Box>
              </Box>
            </Collapse>

            {/* ADDRESS */}
            <Collapse in={isSectionVisible(OWNER_FORM_SECTIONS[1])} timeout={250} unmountOnExit>
              <Box id="address" sx={sectionSx}>
                <Card title="Address">
                  <OwnerAddressFields methods={methods} />
                </Card>
              </Box>
            </Collapse>

            {/* MARKETING */}
            <Collapse in={isSectionVisible(OWNER_FORM_SECTIONS[2])} timeout={250} unmountOnExit>
              <Box id="marketing" sx={sectionSx}>
                <Card title="Marketing">
                  <OwnerMarketingFields methods={methods} />
                </Card>
              </Box>
            </Collapse>

            {visibleSections.length === 0 && (
              <Box sx={{ py: 10, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No fields match "{search}"
                </Typography>
              </Box>
            )}
          </FormProvider>
        </Box>

        <TocSpacer />
      </Box>

      {/* Fixed action bar */}
    </>
  )
}
